<template>
    <div class="ds-single-select flex flex-col gap-1 font-inter font-normal">
        <!-- visually hidden native select -->
        <select
            v-model="model"
            :id="elementId"
            ref="selectElement"
            class="visually-hidden fixed left-offscreen"
            :disabled
            :name="`ds-single-select-${label}`"
            tabIndex="-1"
        >
            <option
                v-for="option in props.options"
                :key="option.id"
                :value="option.value"
            >
                {{ option.label }}
            </option>
        </select>

        <div class="flex min-h-10 w-full flex-col gap-1">
            <label
                v-if="label"
                class="mb-0 text-left text-content-md font-medium text-content-secondary"
                :class="{
                    required,
                    'text-content-disabled': disabled,
                }"
                :for="elementId"
            >
                {{ label }}
            </label>

            <DsPopover
                :open="isOpen && !disabled"
                @update:open="handleOpen"
            >
                <DsPopoverTrigger>
                    <slot name="trigger">
                        <div
                            ref="triggerRef"
                            :class="[
                                'control-wrapper rounded px-3 py-2',
                                {
                                    'cursor-pointer': !disabled,
                                    'is-open': isOpen,
                                    'has-error': hasError,
                                    'is-disabled': disabled,
                                },
                            ]"
                            data-testid="ds-single-select-control"
                            :tabindex="disabled ? -1 : 0"
                            @mousedown.prevent
                        >
                            <div class="flex items-center justify-between gap-2">
                                <div class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-content-lg">
                                    <span v-if="!selectedOption">
                                        {{ placeholder }}
                                    </span>
                                    <span
                                        v-else
                                        class="line-clamp-1"
                                        :class="disabled ? 'text-content-disabled' : 'text-content-primary'"
                                    >
                                        {{ selectedOption?.label }}
                                    </span>
                                </div>
                                <div
                                    class="flex flex-shrink-0 items-center gap-2"
                                    :class="{ 'opacity-50': disabled }"
                                >
                                    <button
                                        v-if="showClearButton"
                                        class="flex items-center justify-center"
                                        @click.stop="handleClearSelection"
                                    >
                                        <DsIcon
                                            class="text-content-primary"
                                            icon="fa-solid fa-circle-xmark"
                                            size="md"
                                        />
                                    </button>
                                    <div class="flex items-center">
                                        <DsIcon
                                            v-if="isOpen && !disabled"
                                            class="text-content-primary"
                                            icon="fa-solid fa-chevron-up"
                                            size="md"
                                        />
                                        <DsIcon
                                            v-else
                                            class="text-content-primary"
                                            icon="fa-solid fa-chevron-down"
                                            size="md"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </slot>
                </DsPopoverTrigger>

                <DsPopoverContent
                    align="start"
                    side="bottom"
                    :sideOffset="8"
                >
                    <div
                        class="max-h-[400px] overflow-y-auto"
                        :style="{ width: 'var(--reka-popover-trigger-width)' }"
                    >
                        <div
                            v-if="hasSearchInput"
                            class="flex flex-col gap-2 border-b border-ds-primary-200 p-2"
                        >
                            <!-- search -->
                            <DsTextInput
                                v-model="searchQuery"
                                autofocus
                                name="ds-single-select-search"
                                :placeholder="searchInputPlaceholder ?? `Search ${label.toLowerCase()}`"
                                type="text"
                                @click.stop
                            >
                                <template #leading>
                                    <DsIcon
                                        class="-ml-2 text-content-primary"
                                        icon="fa-magnifying-glass"
                                        size="md"
                                    />
                                </template>
                                <template
                                    v-if="hasSearchQuery"
                                    #trailing
                                >
                                    <DsIcon
                                        class="-mr-2 cursor-pointer text-content-primary"
                                        icon="fa-solid fa-circle-xmark"
                                        size="md"
                                        @click.stop="searchQuery = ''"
                                    />
                                </template>
                            </DsTextInput>
                        </div>

                        <div
                            v-if="!filteredOptions.length"
                            class="p-6"
                        >
                            <slot name="emptyState">
                                <DsSelectEmptyState
                                    icon="fa-solid fa-magnifying-glass"
                                    subtitle="Adjust your search and try again"
                                    title="No results found"
                                >
                                    <DsButton
                                        size="sm"
                                        variant="tertiary"
                                        @click="searchQuery = ''"
                                    >
                                        Clear search
                                    </DsButton>
                                </DsSelectEmptyState>
                            </slot>
                        </div>

                        <div v-if="!!filteredOptions.length">
                            <ul class="options">
                                <template v-if="isGrouped">
                                    <DsSingleSelectGroup
                                        v-for="(group, index) in filteredOptions"
                                        :key="group.label + '-' + index"
                                        :data-testid="`ds-single-select-group-${group.label}`"
                                        :items="group.items"
                                        :label="group.label ?? ''"
                                    >
                                        <li
                                            v-for="opt in group.items"
                                            :key="opt.id"
                                            class="flex items-center justify-between"
                                        >
                                            <DsSingleSelectOption
                                                :disabled="opt.disabled"
                                                :isSelected="isSelected(opt.id)"
                                                @click="handleSelect(opt)"
                                                @keydown="(event: KeyboardEvent) => handleOptionKeyDown(event, opt)"
                                            >
                                                <template #optionLabelMain>
                                                    <slot
                                                        :isSelected="isSelected(opt.id)"
                                                        name="optionLabelMain"
                                                        :option="opt"
                                                    >
                                                        <span :data-testid="`ds-single-select-option-${opt.id}`">{{
                                                            opt.label
                                                        }}</span>
                                                    </slot>
                                                </template>
                                                <template #optionLabelTrailing>
                                                    <slot
                                                        :isSelected="isSelected(opt.id)"
                                                        name="optionLabelTrailing"
                                                        :option="opt"
                                                    >
                                                        <DsIcon
                                                            v-if="isSelected(opt.id)"
                                                            class="text-content-primary"
                                                            icon="fa-solid fa-check"
                                                            size="md"
                                                        />
                                                    </slot>
                                                </template>
                                            </DsSingleSelectOption>
                                        </li>
                                    </DsSingleSelectGroup>
                                </template>
                                <template v-else>
                                    <div class="p-2">
                                        <li
                                            v-for="opt in filteredOptions[0].items"
                                            :key="opt.id"
                                            class="flex items-center justify-between"
                                        >
                                            <DsSingleSelectOption
                                                class=""
                                                :disabled="opt.disabled"
                                                :isSelected="isSelected(opt.id)"
                                                @click="handleSelect(opt)"
                                                @keydown="(event: KeyboardEvent) => handleOptionKeyDown(event, opt)"
                                            >
                                                <template #optionLabelLeading>
                                                    <slot
                                                        :isSelected="isSelected(opt.id)"
                                                        name="optionLabelLeading"
                                                        :option="opt"
                                                    />
                                                </template>
                                                <template #optionLabelMain>
                                                    <slot
                                                        :isSelected="isSelected(opt.id)"
                                                        name="optionLabelMain"
                                                        :option="opt"
                                                    >
                                                        <span :data-testid="`ds-single-select-option-${opt.id}`">{{
                                                            opt.label
                                                        }}</span>
                                                    </slot>
                                                </template>

                                                <template #optionLabelTrailing>
                                                    <slot
                                                        :isSelected="isSelected(opt.id)"
                                                        name="optionLabelTrailing"
                                                        :option="opt"
                                                    >
                                                        <DsIcon
                                                            v-if="isSelected(opt.id)"
                                                            class="text-content-primary"
                                                            icon="fa-solid fa-check"
                                                            size="md"
                                                        />
                                                    </slot>
                                                </template>
                                            </DsSingleSelectOption>
                                        </li>
                                    </div>
                                </template>
                            </ul>
                        </div>
                    </div>
                </DsPopoverContent>
            </DsPopover>
        </div>

        <div
            v-if="hasError"
            class="flex items-center gap-2 text-content-md text-content-error"
        >
            <DsIcon
                icon="fa-solid fa-triangle-exclamation"
                size="sm"
            />
            <slot name="error">
                {{ errorMessage }}
            </slot>
        </div>

        <div
            v-else-if="!hasError && helperText"
            class="text-content-md text-content-secondary"
            :class="{ 'text-content-disabled': disabled }"
        >
            {{ helperText }}
        </div>
    </div>
</template>

<script setup lang="ts" generic="T extends { id: string | number; disabled?: boolean }">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import DsSingleSelectGroup from '@/components/design-system/singleselect/DsSingleSelectGroup.vue';
import DsSingleSelectOption from '@/components/design-system/singleselect/DsSingleSelectOption.vue';
import type { DsSingleSelectOptionBase, DsSingleSelectProps } from '@/components/design-system/singleselect/single-select.types';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import { onKeyDown } from '@vueuse/core';
import { computed, ref, useId, watch } from 'vue';

const props = withDefaults(defineProps<DsSingleSelectProps<T>>(), {
    helperText: '',
    disabled: false,
    showSearchInput: true,
});

const model = defineModel<T>();
const searchQuery = defineModel('search', { default: '' });

const selectedOption = computed(() => {
    return props.options.find(option => option.id === model.value?.id);
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: T): void;
    (e: 'open'): void;
    (e: 'close'): void;
    (e: 'optionSelected', payload: { option: T & DsSingleSelectOptionBase; selected: boolean }): void;
    (e: 'search', value: string): void;
}>();

const selectElement = ref<HTMLSelectElement>();
const elementId = useId();

const triggerRef = ref<HTMLDivElement>();
const isOpen = ref(false);

const hasSearchInput = computed(() => props.showSearchInput && props.options.length > 5);

const hasSearchQuery = computed(() => {
    return !!searchQuery.value.length;
});

const handleClearSelection = () => {
    if (props.disabled) return;
    model.value = undefined;
};

const isGrouped = computed(() => !!props.groupLabel);

const groupedOptions = computed(() => {
    if (!isGrouped.value) return [{ label: undefined, items: props.options }];

    const groups = new Map<string | undefined, (T & DsSingleSelectOptionBase)[]>();
    props.options.forEach(option => {
        const groupKey = props.groupLabel && String(option[props.groupLabel as keyof (T & DsSingleSelectOptionBase)]);
        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(option);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
        label,
        items,
    }));
});

const filteredOptions = computed(() => {
    if (!props.options.length) return [];
    if (!searchQuery.value) return groupedOptions.value;

    const searchTerm = searchQuery.value;
    const fuzzyRegex = new RegExp(
        searchTerm
            .split('')
            .map((c: string) => `${c}.*`)
            .join('')
            .slice(0, -2),
        'i',
    );

    return groupedOptions.value
        .map(group => ({
            label: group.label,
            items: group.items.filter(item => fuzzyRegex.test(item.label)),
        }))
        .filter(group => !!group.items.length);
});

const showClearButton = computed(() => {
    return selectedOption.value && !props.disabled && !props.required;
});

const isSelected = (id: string | number) => {
    const selected = model.value?.id === id;
    return selected;
};

const handleOpen = (event: boolean) => {
    if (!props.disabled) isOpen.value = event;
};

const handleSelect = (option: T) => {
    if (option.disabled) return;

    model.value = option;
    isOpen.value = false;

    if (triggerRef.value) {
        triggerRef.value.blur();
    }

    emit('optionSelected', { option: option as T & DsSingleSelectOptionBase, selected: true });
};

const handleOptionKeyDown = (event: KeyboardEvent, option: T) => {
    if (option.disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
        handleSelect(option);
    }
};

onKeyDown('Enter', () => {
    if (!isOpen.value && document.activeElement === triggerRef.value && !props.disabled) {
        isOpen.value = true;
    }
});

const hasError = computed(() => !!props.error);
const errorMessage = computed(() => {
    if (typeof props.error === 'string') {
        return props.error;
    }
    return '';
});

const refreshInputValidity = (hasError: boolean) => {
    selectElement.value?.setCustomValidity(hasError ? errorMessage.value || 'Error' : '');
};

watch(hasError, refreshInputValidity);

watch(isOpen, open => {
    if (props.disabled) return;
    if (open) {
        emit('open');
    } else {
        emit('close');
    }
});

watch(model, val => {
    if (val) {
        emit('update:modelValue', val);
    }
});

defineExpose({
    handleOpen,
});
</script>

<style scoped lang="scss">
.control-wrapper {
    @apply bg-primary outline outline-1 outline-ds-primary-400 hover:bg-secondary;

    &:focus:not(.is-open):not(.is-disabled) {
        @apply shadow-outline-field outline-2 outline-ds-gray-800;
    }

    &.is-open:not(.is-disabled) {
        @apply shadow-outline-field outline-2 outline-ds-gray-800;
    }

    &.has-error:not(.is-open) {
        @apply outline-2 outline-ds-red-600;
    }

    &.is-disabled {
        @apply cursor-not-allowed bg-ds-gray-100 text-content-disabled;
    }
}

:deep(.required) {
    &::after {
        content: '*';
        @apply text-content-error;
    }
}
</style>
